"use client"

import React from "react";
import instance from "@/lib/axiosInstance";
import { useDebounce } from "@/hooks/use-debounce";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Calendar1, User } from "lucide-react";
import { Flight } from "@/types/Flight";

import { User as UserResponse } from "@/types/User";
import {
    Calendar,
    MapPin,
    Settings,
    Plane,
    TicketsPlane,
    Bell,
    TowerControl
} from "lucide-react"

import { useNotifications } from "@/context/NotificationsContext";

import {
    CommandDialog,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from "@/components/ui/command"
import { DialogTitle } from "@/components/ui/dialog";

import { useTranslation } from "react-i18next";
import { useRouter } from 'next/navigation'
import { useAuth } from "@/context/AuthContext"

const SearchContext = React.createContext({
    open: false,
    setOpen: (_: boolean) => { },
})

export function SearchProvider({ children }: { children: React.ReactNode }) {
    const router = useRouter()
    const [open, setOpen] = React.useState(false);
    const { unreadCount, openNotifications } = useNotifications();
    const [searchTerm, setSearchTerm] = React.useState("");
    const [searchResults, setSearchResults] = React.useState<{
        users: UserResponse[];
        events: any[];
        airports: any[];
    }>({
        users: [],
        events: [],
        airports: []
    });
    const { t } = useTranslation();
    const { user } = useAuth();
    const [liveFlight, setLiveFlight] = React.useState<Flight | undefined>(undefined);

    const debouncedSearchTerm = useDebounce(searchTerm, 500);

    React.useEffect(() => {
        const fetchLiveFlight = async () => {
            if (!user) return;

            try {
                const response = await instance.get(`/${user?.username}/flights?status=active`)
                if (response.data.success) {
                    setLiveFlight(response.data.data[0] || undefined);
                }
            } catch (error) {
                console.error("Error fetching flights:", error);
            }
        };

        fetchLiveFlight();
    }, [user]);

    React.useEffect(() => {
        const performSearch = async () => {
            if (!debouncedSearchTerm || debouncedSearchTerm.length < 2) {
                setSearchResults({ users: [], events: [], airports: [] });
                return;
            }

            try {
                const response = await instance.get(`/search/${debouncedSearchTerm}`);
                if (response.data.success) {
                    const data = response.data.data || {};
                    setSearchResults({
                        users: data.users || [],
                        events: data.events || [],
                        airports: data.airports || []
                    });
                }
            } catch (error) {
                console.error("Error performing search:", error);
                setSearchResults({ users: [], events: [], airports: [] });
            }
        };

        performSearch();
    }, [debouncedSearchTerm]);

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
    };

    React.useEffect(() => {
        const down = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "k") {
                e.preventDefault()
                setOpen((prev) => !prev)
            }
        }

        document.addEventListener("keydown", down)
        return () => document.removeEventListener("keydown", down)
    }, [])

    const handleLinkClick = (url: string) => {
        router.push(url);
        setTimeout(() => {
            setOpen(false);
        }, 10);
    };

    return (
        <SearchContext.Provider value={{ open, setOpen }}>
            {children}
            <CommandDialog open={open} onOpenChange={setOpen} commandProps={{
                shouldFilter: false
            }}>
                <DialogTitle className="sr-only">{t('search.title')}</DialogTitle>
                <CommandInput placeholder={t('search.placeholder')} value={searchTerm} onValueChange={handleSearchChange} />
                <CommandList>
                    <CommandEmpty>{t('search.no_results')}</CommandEmpty>

                    {searchTerm && (
                        (searchResults.users.length > 0 ||
                            searchResults.events.length > 0 ||
                            searchResults.airports.length > 0)
                    ) && (
                            <>
                                {/* Users results */}
                                {searchResults.users.length > 0 && (
                                    <>
                                        <CommandGroup heading={t('search.users')}>
                                            {searchResults.users.map((user) => (
                                                <CommandItem
                                                    key={`user-${user.id}`}
                                                    onSelect={() => {
                                                        handleLinkClick(`/${user.username}`)
                                                    }}
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <Avatar className="h-6 w-6">
                                                            <AvatarImage src={user.avatar} alt={user.username} />
                                                            <AvatarFallback>
                                                                <User className="h-4 w-4" />
                                                            </AvatarFallback>
                                                        </Avatar>
                                                        <div className="flex flex-col">
                                                            <span>{user.displayName}</span>
                                                            <span className="text-xs text-muted-foreground">
                                                                @{user.username}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </CommandItem>
                                            ))}
                                        </CommandGroup>
                                        <CommandSeparator />
                                    </>
                                )}

                                {/* Events results */}
                                {searchResults.events.length > 0 && (
                                    <CommandGroup heading={t('search.events')}>
                                        {searchResults.events.map((event) => (
                                            <CommandItem
                                                key={`event-${event.id}`}
                                                onSelect={() => {
                                                    handleLinkClick("/dispatch/events/" + event.id)
                                                }}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <Calendar className="h-4 w-4" />
                                                    <span>{event.name}</span>
                                                    <span className="text-xs text-muted-foreground ml-auto">
                                                        {new Date(event.start_date).toLocaleDateString()}
                                                    </span>
                                                </div>
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                )}

                                {/* Airports results */}
                                {searchResults.airports.length > 0 && (
                                    <CommandGroup heading={t('search.airports')}>
                                        {searchResults.airports.map((airport) => (
                                            <CommandItem
                                                key={`airport-${airport.id}`}
                                                onSelect={() => {
                                                    handleLinkClick(`/dispatch/airport/${airport.icao}`)
                                                }}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <MapPin className="h-4 w-4" />
                                                    <span>{airport.name}</span>
                                                    <span className="font-mono text-xs ml-1">
                                                        {airport.icao}
                                                    </span>
                                                    {airport.iata && (
                                                        <span className="text-xs text-muted-foreground ml-1">
                                                            ({airport.iata})
                                                        </span>
                                                    )}
                                                </div>
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                )}

                                <CommandSeparator />
                            </>
                        )}

                    {user && liveFlight && (
                        <CommandGroup heading={t('search.flights')}>
                            <CommandItem className="bg-green-950/40 border border-green-950/40 hover:bg-green-950 data-[selected='true']:bg-green-950" onSelect={() => { handleLinkClick(`/${user.username}/flight/${liveFlight.flight_key}`) }}>
                                <Plane />
                                <p>
                                    <span className="font-bold">LIVE: </span>
                                    {liveFlight?.callsign}
                                    <span className="text-muted-foreground text-xs ml-2">
                                        {liveFlight?.departure?.iata} - {liveFlight?.arrival?.iata}
                                    </span>
                                </p>
                            </CommandItem>
                        </CommandGroup>
                    )}
                    <CommandSeparator />
                    <CommandGroup heading={t('search.suggestions')}>
                        <CommandItem onSelect={() => { handleLinkClick(`/dispatch/contracts`) }}>
                            <TicketsPlane />
                            <span>{t('search.contracts')}</span>
                        </CommandItem>
                        <CommandItem onSelect={() => { handleLinkClick(`/dispatch/focus-airports`) }}>
                            <TowerControl />
                            <span>{t('search.focus_airports')}</span>
                        </CommandItem>
                        <CommandItem onSelect={() => { handleLinkClick(`/community/events`) }}>
                            <Calendar1 />
                            <span>{t('search.events')}</span>
                        </CommandItem>
                    </CommandGroup>
                    <CommandSeparator />
                    <CommandGroup heading={t('search.actions')}>
                        <CommandItem onSelect={() => { setOpen(false); openNotifications() }} className="relative">
                            <Bell />
                            <span>{t('search.notifications')}</span>
                            {unreadCount > 0 &&
                                <span className="ml-auto text-center font-bold text-xs rounded-full size-4 aspect-square bg-amber-600">{unreadCount}</span>
                            }
                        </CommandItem>
                        <CommandItem onSelect={() => { handleLinkClick('/settings') }}>
                            <Settings />
                            <span>{t('search.settings')}</span>
                        </CommandItem>
                    </CommandGroup>
                </CommandList>
            </CommandDialog>
        </SearchContext.Provider>
    )
}

export function useSearch() {
    const context = React.useContext(SearchContext)
    if (!context) throw new Error("useSearch must be used within SearchProvider")
    return context
}
